'use client';
import { LegalModal, PolicyType, useLegalPolicies } from '@practium/features/policy';
import { LoadingState } from '@practium/ui';
import { useState } from 'react';
import { ContributionHeatmap } from '../components/ContributionHeatmap/ContributionHeatmap';
import { LegalLinksCard } from '../components/LegalLinksCard/LegalLinksCard';
import { PersonalInfo } from '../components/PersonalInfo/PersonalInfo';
import { ProfileHeader } from '../components/ProfileHeader/ProfileHeader';
import { ProfilePlanUpgradeCard } from '../components/ProfilePlanUpgradeCard/ProfilePlanUpgradeCard';
import { ProfileStatsCard } from '../components/ProfileStatsCard/ProfileStatsCard';
import type { ProfileData } from '../types/profile.types';
import styles from './ProfilePage.module.css';

interface ProfilePageProps {
	data: ProfileData | null;
	isLoading?: boolean;
	onModify?: () => void;
	onUpgrade?: () => void;
}

export const ProfilePage = ({ data, isLoading, onModify, onUpgrade }: ProfilePageProps) => {
	const { activeLegalLinks, isLoading: isPoliciesLoading } = useLegalPolicies();
	const [activeModal, setActiveModal] = useState<PolicyType | null>(null);

	if (isLoading || isPoliciesLoading || !data) {
		return <LoadingState fullScreen />;
	}

	const { user, stats, contributions, plan } = data;

	return (
		<div className={styles.profilePage}>
			<div className={styles.headerWrapper}>
				<ProfileHeader
					name={`${user.name} ${user.surName || ''}`}
					email={user.email}
					avatar={user.avatar?.url}
					backgroundUrl={user.background?.url}
					onModify={onModify}
				/>
			</div>
			<div className={styles.container}>
				<div className={styles.contentGrid}>
					<main className={styles.leftColumn}>
						<div className={styles.statsRow}>
							<ProfileStatsCard
								title="Anos de Practium"
								subtitle="Anos de utilização"
								value={stats.yearsOfUse}
								label="Anos"
							/>
							<ProfileStatsCard
								title="Sessões diárias"
								subtitle="Atividade na plataforma"
								value={stats.activeDaysPercentage}
								label={`${stats.activeDaysPercentage}%`}
							/>
						</div>
						<div className={styles.heatmapContainer}>
							<ContributionHeatmap
								contributions={contributions.contributions}
								totalCount={contributions.totalContributions}
								percentile={contributions.percentile}
							/>
						</div>
						<div className={styles.planContainer}>
							<ProfilePlanUpgradeCard
								planName={plan.name}
								description={plan.description}
								validUntil={plan.validUntil}
								canUpgrade={plan.canUpgrade}
								upgradePlanName={plan.upgradePlanName}
								onUpgrade={onUpgrade}
							/>
						</div>
					</main>
					<aside className={styles.rightColumn}>
						<PersonalInfo {...user} />
						<LegalLinksCard links={activeLegalLinks} onOpenModal={setActiveModal} />
					</aside>
				</div>
			</div>

			{activeLegalLinks.map((modal) => (
				<LegalModal
					key={modal.key}
					open={activeModal === modal.key}
					onClose={() => setActiveModal(null)}
					policy={modal.policy}
				/>
			))}
		</div>
	);
};
